import * as v from 'valibot';

import {
  branchSchema,
  cargoTypeSchema,
  carrierSchema,
  carrierServiceSchema,
  currencySchema,
  equipmentTypeSchema,
  linerTypeSchema,
  pricingTypeSchema,
  tariffTypeSchema,
} from '@/modules/entities';

const option = {
  id: v.string(),
  label: v.string(),
};

const nullableString = v.nullable(v.string());

// TODO - refactor it when api will be implemented

//  Base tariff (shared fields)
export const tariffBaseApiSchema = v.object({
  branchDescription: nullableString,
  branchKey: v.number(),
  cargoTypeCode: v.string(),
  cargoTypeDescription: nullableString,
  carrierKey: v.number(),
  carrierName: nullableString,
  customerKey: v.number(),
  customerName: nullableString,
  effectiveDate: v.string(),
  expirationDate: v.string(),
  linerType: nullableString,
  linerTypeDescription: nullableString,
  pricingType: v.string(),
  pricingTypeDescription: nullableString,
  serviceDescription: nullableString,
  serviceKey: v.number(),
  tariffKey: v.number(),
  tariffName: v.string(),
  tariffType: v.string(),
  tariffTypeDescription: nullableString,
});

// Tariffs list item (BE erase fields to null whenever they want)
export const tariffSummaryApiSchema = v.object({
  ...tariffBaseApiSchema.entries,

  createdBy: v.number(),
  createdByName: nullableString,
  createdOn: v.string(),
  modifiedBy: v.nullable(v.number()),
  modifiedByName: nullableString,
  modifiedOn: nullableString,
});

export const tariffSummarySchema = v.object({
  // ...v.omit(tariffApiSchema, ['createdOn', 'effectiveFrom', 'effectiveTo', 'modifiedOn']).entries,
  ...tariffSummaryApiSchema.entries,
});

// Tariffs list response
export const tariffsApiSchema = v.object({
  items: v.array(tariffSummaryApiSchema),
  totalCount: v.number(),
});

export type TariffSummaryApi = v.InferOutput<typeof tariffSummaryApiSchema>;
export type TariffSummary = v.InferOutput<typeof tariffSummarySchema>;
// TODO: write normalizer after api contract will defined
// normalize
// createdAtLabel: format(data.createdAt, 'DD-MM-YYYY')

// Tariff Details Item (table row)
export const tariffDetailsItemApiSchema = v.object({
  accountNumber: v.string(),
  charge: v.number(),
  charge2: v.number(),
  class: v.string(),
  cost: v.number(),
  createdBy: v.number(),
  createdOn: v.string(),
  customerLaneId: nullableString,
  customerNotes: nullableString,
  dimDivisor: v.number(),
  distanceFrom: v.number(),
  distanceTo: v.number(),
  distanceType: v.string(),
  effectiveDate: v.string(),
  expirationDate: v.string(),
  laneCode: v.string(),
  laneKey: v.number(),
  liner: v.string(),
  maximumCharge: v.number(),
  minimumCharge: v.number(),
  modifiedBy: v.nullable(v.number()),
  modifiedOn: nullableString,
  pricingBasis: v.string(),
  pricingBasis2: v.string(),
  productCode: v.string(),
  shipmentType: v.nullable(v.string()),
  tariffDetailKey: v.number(),
  tariffKey: v.number(),
  unitsFrom: v.number(),
  unitsTo: v.number(),
  unitType: v.string(),
  weightFrom: v.number(),
  weightTo: v.number(),
  weightType: v.string(),
});

export const tariffDetailsApiSchema = v.object({
  items: v.array(tariffDetailsItemApiSchema),
  totalCount: v.number(),
});

/**
 * Query params for GET /api/pricing/tariff-detail/search.
 */
export const tariffDetailsParamsSchema = v.object({
  accountNumber: v.optional(v.string()),
  charge: v.optional(v.number()),
  charge2: v.optional(v.number()),
  class: v.optional(v.string()),
  cost: v.optional(v.number()),
  customerLaneId: v.optional(v.string()),
  customerNotes: v.optional(v.string()),
  dimDivisor: v.optional(v.number()),
  distanceFrom: v.optional(v.number()),
  distanceTo: v.optional(v.number()),
  distanceType: v.optional(v.string()),
  effectiveDateFrom: v.optional(v.string()),
  effectiveDateTo: v.optional(v.string()),
  laneCode: v.optional(v.string()),
  laneKey: v.optional(v.number()),
  limit: v.optional(v.pipe(v.number(), v.minValue(0)), 10),
  liner: v.optional(v.string()),
  maximumCharge: v.optional(v.number()),
  minimumCharge: v.optional(v.number()),
  offset: v.optional(v.pipe(v.number(), v.minValue(0)), 0),
  pricingBasis: v.optional(v.string()),
  pricingBasis2: v.optional(v.string()),
  productCode: v.optional(v.string()),
  shipmentType: v.optional(v.string()),
  sorting: v.optional(v.string()),
  tariffDetailKey: v.optional(v.number()),
  tariffKey: v.number(),
  unitsFrom: v.optional(v.number()),
  unitsTo: v.optional(v.number()),
  unitType: v.optional(v.string()),
  weightFrom: v.optional(v.number()),
  weightTo: v.optional(v.number()),
  weightType: v.optional(v.string()),
});

export type TariffDetailsApi = v.InferOutput<typeof tariffDetailsApiSchema>;
export type TariffDetailsParams = v.InferOutput<typeof tariffDetailsParamsSchema>;

// Tariff (tariff header)
export const tariffApiSchema = v.object({
  ...tariffSummaryApiSchema.entries,

  carrierCorporation: v.string(),
  currencyCode: v.string(),
  isActive: v.boolean(),
  packingCode: nullableString,
  packingDescription: nullableString,
  pricingBasis: v.string(),
  pricingBasisDescription: nullableString,
  tariffDescription: v.string(),
  tariffStartDate: nullableString,
  trailerKey: v.number(),
});

export type TariffApi = v.InferOutput<typeof tariffApiSchema>;

export const tariffSchema = v.object({
  branch: branchSchema,
  cargoType: cargoTypeSchema,
  carrier: carrierSchema,
  carrierCorporation: v.string(),
  createdBy: v.number(),
  createdOn: v.string(),
  currency: currencySchema,
  customerKey: v.number(),
  effectiveDate: v.string(),
  equipment: equipmentTypeSchema,
  expirationDate: v.string(),
  isActive: v.boolean(),
  linerType: v.nullable(linerTypeSchema),
  modifiedBy: v.nullable(v.number()),
  modifiedOn: v.string(),
  pricingType: pricingTypeSchema,
  service: carrierServiceSchema,
  tariffKey: v.number(),
  tariffName: v.string(),
  tariffStartDate: v.string(),
  tariffType: tariffTypeSchema,
});

export type Tariff = v.InferOutput<typeof tariffSchema>;

// TODO: Add export Option type to common entities

const selectOption = <T extends v.GenericSchema>(id: T, message?: string) =>
  v.object({ id, label: v.string() }, message);

// Tariff details filter
const dateRangeSchema = v.object({
  end: v.string(),
  start: v.string(),
});

export const tariffDetailsFiltersSchema = v.object({
  accountNumber: v.optional(v.string()),
  class: v.optional(v.nullable(v.object(option))),
  effectiveDate: v.optional(v.nullable(dateRangeSchema)),
  laneKey: v.optional(v.nullable(selectOption(v.number()))),
  liner: v.optional(v.nullable(v.object(option))),
  productDescription: v.optional(v.string()),
  shipmentType: v.optional(v.nullable(v.object(option))),
});

export const tariffDetailsFiltersFormSchema = v.object({
  ...tariffDetailsFiltersSchema.entries,
});

export const tariffDetailsFiltersFormPartialSchema = v.object({
  ...v.partial(tariffDetailsFiltersFormSchema).entries,
});

export type TariffDetailsFilters = v.InferOutput<typeof tariffDetailsFiltersSchema>;
export type TariffDetailsFiltersForm = v.InferOutput<typeof tariffDetailsFiltersFormSchema>;

/**
 * API contract for tariffs search endpoint, search query params.
 * Also used to validate URL search params on the client side.
 */
export const tariffsSearchParamsSchema = v.object({
  branchKey: v.optional(v.number()),
  cargoType: v.optional(v.string()),
  carrierKey: v.optional(v.number()),
  currencyCode: v.optional(v.string()),
  customerLaneId: v.optional(v.string()),
  effectiveDate: v.optional(v.pipe(v.string(), v.isoTimestamp())), // '2026-03-09T16:34:19.174Z' UTC
  expirationDate: v.optional(v.pipe(v.string(), v.isoTimestamp())), // '2026-03-09T16:34:19.174Z' UTC
  laneKey: v.optional(v.number()),
  linerType: v.optional(v.string()),
  packingCode: v.optional(v.string()),
  pricingType: v.optional(v.string()),
  serviceKey: v.optional(v.number()),
  skip: v.optional(v.number()),
  sorting: v.optional(v.string()),
  take: v.optional(v.number()),
  tariffKey: v.optional(v.number()),
  tariffName: v.optional(v.string()),
  tariffType: v.optional(v.string()),
  trailerKey: v.optional(v.number()),
});

export type TariffsSearchParams = v.InferInput<typeof tariffsSearchParamsSchema>;

/**
 * Tariffs search filters form
 */
export const tariffsSearchFiltersFormSchema = v.pipe(
  v.object({
    branchKey: v.optional(v.nullable(selectOption(v.number()))),
    cargoType: v.optional(v.nullable(selectOption(v.string()))),
    carrierKey: v.optional(v.nullable(selectOption(v.number()))),
    currencyCode: v.optional(v.nullable(selectOption(v.string()))),
    customerLaneId: v.optional(v.nullable(v.string())),
    effectiveDate: v.pipe(v.string(), v.nonEmpty('Date is required'), v.isoTimestamp('Please enter a valid date')),
    expirationDate: v.optional(v.pipe(v.string(), v.isoTimestamp('Please enter a valid date'))),
    laneKey: v.optional(v.nullable(selectOption(v.number()))),
    linerType: v.optional(v.nullable(selectOption(v.string()))),
    packingCode: v.optional(v.nullable(selectOption(v.string()))),
    pricingType: v.optional(v.nullable(selectOption(v.string()))),
    serviceKey: v.optional(v.nullable(selectOption(v.number()))),
    tariffKey: v.optional(v.nullable(v.string())),
    tariffName: v.optional(v.nullable(v.string())),
    tariffType: v.optional(v.nullable(selectOption(v.string()))),
    trailerKey: v.optional(v.nullable(selectOption(v.number()))),
  }),
  v.forward(
    v.check((data) => {
      if (!data.expirationDate) {
        return true;
      }

      return new Date(data.expirationDate) >= new Date(data.effectiveDate);
    }, 'To date should be bigger than From date'),
    ['expirationDate'],
  ),
);

export type TariffsSearchFiltersFormValues = v.InferInput<typeof tariffsSearchFiltersFormSchema>;
